"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import {
  refreshKeycloakToken,
  KeycloakTokenResponse,
  refreshCustomerToken,
  CustomerRefreshResponse,
} from "@/lib/api";

interface AuthContextType {
  isAuthenticated: boolean;
  accessToken: string | null;
  keycloakToken: string | null;
  customerId: number | null;
  login: (token: string, refreshToken: string, expiresIn?: number) => void;
  loginWithKeycloak: (
    keycloakToken: string,
    backendToken: string,
    refreshToken: string,
    expiresIn?: number,
  ) => void;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ─── token helpers ────────────────────────────────────────────────────────────

function decodeCustomerId(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    // adjust the claim key to match your JWT — common options below
    const raw =
      payload.customerId ?? payload.customer_id ?? payload.sub ?? null;
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
}

// loginType: "customer" = email/password, "keycloak" = Google/OAuth
function saveTokens(
  token: string,
  refreshToken: string,
  expiresIn: number,
  keycloakToken?: string,
  loginType: "customer" | "keycloak" = "customer",
) {
  localStorage.setItem("authToken", token);
  localStorage.setItem("customerToken", token);
  localStorage.setItem("refreshToken", refreshToken);
  const expiry = Date.now() + (expiresIn - 60) * 1000;
  localStorage.setItem("tokenExpiry", expiry.toString());
  localStorage.setItem("loginType", loginType);
  if (keycloakToken) localStorage.setItem("keycloakToken", keycloakToken);
}

function clearTokens() {
  localStorage.removeItem("authToken");
  localStorage.removeItem("customerToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("tokenExpiry");
  localStorage.removeItem("keycloakToken");
  localStorage.removeItem("loginType");
}

// ─── provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [keycloakToken, setKeycloakToken] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── smart refresh: picks the right endpoint based on login type ───────────
  const doRefresh = async (storedRefreshToken: string): Promise<boolean> => {
    const loginType = localStorage.getItem("loginType") ?? "customer";

    if (loginType === "keycloak") {
      // Google/OAuth users — use Keycloak direct endpoint
      const res = await refreshKeycloakToken(storedRefreshToken);
      if (res.success && res.data) {
        const data = res.data as KeycloakTokenResponse;
        const newExpiry = data.expires_in ?? 300;
        saveTokens(
          data.access_token,
          data.refresh_token,
          newExpiry,
          undefined,
          "keycloak",
        );
        setAccessToken(data.access_token);
        setCustomerId(decodeCustomerId(data.access_token));
        setIsAuthenticated(true);
        scheduleRefresh(newExpiry * 1000);
        return true;
      }
      return false;
    } else {
      // Email/password users — use backend Customer refresh endpoint
      const res = await refreshCustomerToken(storedRefreshToken);
      if (res.success && res.data) {
        const data = res.data as CustomerRefreshResponse;
        saveTokens(
          data.accessToken,
          data.refreshToken,
          data.expiresIn,
          undefined,
          "customer",
        );
        setAccessToken(data.accessToken);
        setCustomerId(decodeCustomerId(data.accessToken));
        setIsAuthenticated(true);
        scheduleRefresh(data.expiresIn * 1000);
        return true;
      }
      return false;
    }
  };

  // ── schedule proactive background refresh ─────────────────────────────────
  const scheduleRefresh = (msUntilExpiry: number) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    const delay = Math.max(msUntilExpiry - 90_000, 0);

    refreshTimerRef.current = setTimeout(async () => {
      const storedRefresh = localStorage.getItem("refreshToken");
      if (!storedRefresh) return;
      const success = await doRefresh(storedRefresh);
      if (!success) performLogout();
    }, delay);
  };

  // ── restore session on mount ───────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("authToken");
    const expiry = localStorage.getItem("tokenExpiry");
    const storedRefresh = localStorage.getItem("refreshToken");

    if (token && expiry) {
      const msLeft = parseInt(expiry, 10) - Date.now();

      if (msLeft > 0) {
        // Token still valid — restore immediately
        setAccessToken(token);
        setCustomerId(decodeCustomerId(token));
        const kToken = localStorage.getItem("keycloakToken");
        if (kToken) setKeycloakToken(kToken);
        setIsAuthenticated(true);
        scheduleRefresh(msLeft);
        setLoading(false);
      } else if (storedRefresh) {
        // Token expired but refresh token exists — try to refresh
        doRefresh(storedRefresh)
          .then((success) => {
            if (!success) clearTokens();
          })
          .catch(() => clearTokens())
          .finally(() => setLoading(false));
        return;
      } else {
        clearTokens();
        setLoading(false);
      }
    } else {
      setLoading(false);
    }

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── email/password login ───────────────────────────────────────────────────
  const login = (token: string, refreshToken: string, expiresIn = 300) => {
    saveTokens(token, refreshToken, expiresIn, undefined, "customer");
    setAccessToken(token);
    setCustomerId(decodeCustomerId(token));
    setIsAuthenticated(true);
    scheduleRefresh(expiresIn * 1000);
  };

  // ── Google/OAuth login ────────────────────────────────────────────────────
  const loginWithKeycloakFn = (
    kToken: string,
    backendToken: string,
    refreshToken: string,
    expiresIn = 300,
  ) => {
    saveTokens(backendToken, refreshToken, expiresIn, kToken, "keycloak");
    setAccessToken(backendToken);
    setKeycloakToken(kToken);
    setCustomerId(decodeCustomerId(backendToken));
    setIsAuthenticated(true);
    scheduleRefresh(expiresIn * 1000);
  };

  const performLogout = () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    clearTokens();
    setAccessToken(null);
    setKeycloakToken(null);
    setCustomerId(null);
    setIsAuthenticated(false);
  };

  const logout = () => {
    performLogout();
    router.push("/customer/sign-in");
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        accessToken,
        keycloakToken,
        customerId,
        login,
        loginWithKeycloak: loginWithKeycloakFn,
        logout,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
